// Pure helpers of `excel_get_workbook_integrity` and `excel_inspect_cell` (AUDIT_SPEC §2.1). No Office.js.
import { colToLetter, parseRange, splitSheetPrefix } from './a1';

export type FileExtension = 'xlsx' | 'xlsm' | 'xlsb' | 'xls' | 'other';

/** File extension of a workbook name ("Model v3.xlsm" → xlsm; an unsaved "Book1" → other). */
export function fileExtensionOf(name: string | null | undefined): FileExtension {
  const m = /\.(xlsx|xlsm|xlsb|xls)$/i.exec(String(name ?? '').trim());
  return m ? (m[1].toLowerCase() as FileExtension) : 'other';
}

/** A defined name whose target is gone: Office reports `type === 'Error'` or a `#REF!` formula. */
export function isBrokenName(type: string | null | undefined, refersTo: string | null | undefined): boolean {
  return String(type ?? '') === 'Error' || /#REF!/i.test(String(refersTo ?? ''));
}

/** The name points at one row or one column of ≥ `min` cells (a series, e.g. `=Calc!$D$5:$AK$5`). */
export function nameOnSeries(refersTo: string | null | undefined, min = 4): boolean {
  const raw = String(refersTo ?? '').trim().replace(/^=/, '');
  if (!raw || raw.includes(',') || raw.includes('(')) return false;
  const { address } = splitSheetPrefix(raw);
  try {
    const r = parseRange(address.replace(/\$/g, ''));
    const rows = r.end.row - r.start.row + 1;
    const cols = r.end.col - r.start.col + 1;
    return (rows === 1 && cols >= min) || (cols === 1 && rows >= min);
  } catch {
    return false;
  }
}

/** Cells of a sheet-qualified area, row-major, at most `max` (used to expand precedent areas). */
export function expandArea(qualified: string, max: number): { sheet: string | null; cell: string }[] {
  const { sheet, address } = splitSheetPrefix(qualified);
  const out: { sheet: string | null; cell: string }[] = [];
  let r;
  try {
    r = parseRange(address.replace(/\$/g, ''));
  } catch {
    return out;
  }
  for (let row = r.start.row; row <= r.end.row && out.length < max; row++) {
    for (let col = r.start.col; col <= r.end.col && out.length < max; col++) out.push({ sheet, cell: `${colToLetter(col)}${row}` });
  }
  return out;
}

/** Total cells over sheet-qualified areas (whole rows / columns count to Excel's limits). */
export function areasCells(addresses: readonly string[]): number {
  let n = 0;
  for (const a of addresses) {
    try {
      const r = parseRange(splitSheetPrefix(a).address.replace(/\$/g, ''));
      n += (r.end.row - r.start.row + 1) * (r.end.col - r.start.col + 1);
    } catch {
      /* skip unparsable addresses */
    }
  }
  return n;
}

/** True when `cell` (bare A1) on `sheet` lies inside one of the sheet-qualified areas. */
export function areasContain(addresses: readonly string[], sheet: string, cell: string): boolean {
  let c;
  try {
    c = parseRange(cell).start;
  } catch {
    return false;
  }
  for (const a of addresses) {
    const { sheet: s, address } = splitSheetPrefix(a);
    if (s !== null && s !== sheet) continue;
    try {
      const r = parseRange(address.replace(/\$/g, ''));
      if (c.row >= r.start.row && c.row <= r.end.row && c.col >= r.start.col && c.col <= r.end.col) return true;
    } catch {
      /* skip */
    }
  }
  return false;
}

/** First text cell to the left of a precedent on its row: `leftValues` runs from the farthest cell to the nearest. */
export function labelFromLeft(leftValues: readonly unknown[] | null | undefined): string | null {
  if (!leftValues?.length) return null;
  for (let i = leftValues.length - 1; i >= 0; i--) {
    const v = leftValues[i];
    if (typeof v === 'string' && v.trim() && !v.startsWith('#')) return v.trim().slice(0, 80);
  }
  return null;
}
